import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, Keyboard, X } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Label } from './Label';

/**
 * Single-field dialog — the replacement for `window.prompt`.
 *
 * `window.prompt` fails the same way `window.confirm` did, and one way worse. It cannot be
 * styled, so the shop's dialog looks like a browser security notice; it renders the bare origin
 * ("mecaradar.com says…"), which is the exact chrome a phishing warning uses; it blocks the JS
 * thread; and — the one this fixes that confirm never had — it gives a single untyped, unlabelled,
 * unvalidated line. A counter typing an EAN under a dead barcode got no field label, no hint about
 * the format, no minimum length, and no way to be told "that is too short" other than a second
 * dialog after the round trip.
 *
 * So the field here carries its own label, placeholder, hint and validation, and the confirming
 * button stays disabled until the value could plausibly be right.
 *
 * Two ways to use it, like ConfirmDialog:
 *   • declaratively, rendering <PromptDialog open={…} …/>, or
 *   • via `usePrompt()`, which returns a promise and needs no local open/close state:
 *
 *       const [prompt, promptDialog] = usePrompt();
 *       const code = await prompt({ title: 'Type a code', minLength: 3 });
 *       if (!code) return;                       // cancelled — same shape as window.prompt
 *
 * Accessibility: focus moves to the FIELD on open (not the button — the operator is here to
 * type) and is restored on close, Escape cancels, Enter submits a single-line field, and focus
 * is trapped inside the dialog while it is open.
 */
export interface PromptOptions {
  /** Short imperative headline — "Type a code". */
  title: string;
  /** Why this dialog is in front of them, and what to type. */
  description?: string;
  /** Field label. Name the FORMAT expected, not the field ("EAN, SKU or manufacturer ref."). */
  label?: string;
  placeholder?: string;
  initialValue?: string;
  /** Quiet line under the field — an example, or where to read the value off the part. */
  hint?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Below this length the confirming button stays disabled. Default 1: no empty answers. */
  minLength?: number;
  maxLength?: number;
  /** A reason or a comment rather than a code — taller field, Enter inserts a newline. */
  multiline?: boolean;
  /** Codes and references read better in a monospaced face, and misread less. */
  mono?: boolean;
  /** Return a message to refuse the value, or null to accept it. Runs on every keystroke. */
  validate?: (value: string) => string | null;
}

interface PromptDialogProps extends PromptOptions {
  open: boolean;
  /** Truthy while the submitted value is being acted on — disables the field and both buttons. */
  busy?: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

export function PromptDialog({
  open,
  title,
  description,
  label,
  placeholder,
  initialValue = '',
  hint,
  confirmLabel,
  cancelLabel,
  minLength = 1,
  maxLength,
  multiline = false,
  mono = false,
  validate,
  busy = false,
  onSubmit,
  onCancel,
}: PromptDialogProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const [value, setValue] = useState(initialValue);
  // Only shown once the operator has typed something: a dialog that opens already complaining
  // about an empty field is nagging, not helping.
  const [touched, setTouched] = useState(false);

  // Each opening starts clean, including after a cancel.
  useEffect(() => {
    if (!open) return;
    setValue(initialValue);
    setTouched(false);
    restoreRef.current = document.activeElement as HTMLElement | null;
    // The operator opened this to type. Put the caret where they are already looking.
    fieldRef.current?.focus();
    fieldRef.current?.select();
    return () => restoreRef.current?.focus?.();
  }, [open, initialValue]);

  const trimmed = value.trim();
  const problem = trimmed.length >= minLength ? (validate?.(trimmed) ?? null) : null;
  const submittable = trimmed.length >= minLength && !problem && !busy;

  const submit = useCallback(() => {
    if (!submittable) {
      setTouched(true);
      return;
    }
    onSubmit(trimmed);
  }, [submittable, onSubmit, trimmed]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault();
        onCancel();
        return;
      }
      // Enter submits a one-line field, exactly as the native prompt did. In a multiline field
      // it belongs to the text, so only Ctrl/⌘+Enter submits.
      if (e.key === 'Enter' && !busy && (!multiline || e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        submit();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled])'
      );
      if (!focusables?.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, multiline, onCancel, submit]);

  if (!open) return null;

  // Red field once they have engaged with it and the value still cannot be submitted.
  const showProblem = touched && (!!problem || trimmed.length < minLength);
  const fieldClass = mono ? 'font-mono tracking-wide' : undefined;

  return createPortal(
    <div
      // Above the application's own modals (`.mds-modal-bd` is z-index 60): this replaces a
      // native dialog, and a native dialog was always on top of everything.
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={() => !busy && onCancel()}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="prompt-title"
        aria-describedby={description ? 'prompt-description' : undefined}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border bg-card shadow-lg"
      >
        <div className="flex gap-3 p-5">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Keyboard className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="prompt-title" className="text-base font-semibold text-foreground">
              {title}
            </h2>
            {description && (
              <p id="prompt-description" className="mt-1 text-sm text-muted-foreground">
                {description}
              </p>
            )}

            <div className="mt-4 space-y-1.5">
              {label && <Label htmlFor="prompt-field">{label}</Label>}
              {multiline ? (
                <Textarea
                  id="prompt-field"
                  ref={fieldRef}
                  rows={4}
                  className={fieldClass}
                  value={value}
                  placeholder={placeholder}
                  maxLength={maxLength}
                  disabled={busy}
                  aria-invalid={showProblem || undefined}
                  onBlur={() => setTouched(true)}
                  onChange={(e) => setValue(e.target.value)}
                />
              ) : (
                <Input
                  id="prompt-field"
                  ref={fieldRef}
                  className={fieldClass}
                  value={value}
                  placeholder={placeholder}
                  maxLength={maxLength}
                  disabled={busy}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-invalid={showProblem || undefined}
                  onBlur={() => setTouched(true)}
                  onChange={(e) => setValue(e.target.value)}
                />
              )}
              {problem && touched ? (
                <p className="text-xs text-destructive">{problem}</p>
              ) : (
                hint && <p className="text-xs text-muted-foreground">{hint}</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}>
            <X className="h-4 w-4" />
            {cancelLabel ?? t('ui.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={!submittable}>
            <Check className="h-4 w-4" />
            {busy ? t('ui.working') : (confirmLabel ?? t('ui.confirm'))}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Promise-based prompt, so a handler reads the way `window.prompt` did:
 *
 *     const code = await prompt({ title: 'Type a code', minLength: 3 });
 *     if (!code) return;                        // cancelled, or nothing typed
 *
 * Resolves to the TRIMMED value, or null on cancel — the same two outcomes the native dialog
 * had, so converting a call site is a one-line change plus `async`.
 *
 * Returns `[prompt, dialog, open]` — render `dialog` once anywhere in the component. `open` is
 * there for the keyboard: a screen that listens for a barcode wedge on `window` has to stop
 * while the operator is typing into this field, or the same characters are counted twice.
 */
export function usePrompt(): [
  (opts: PromptOptions) => Promise<string | null>,
  React.ReactNode,
  boolean,
] {
  const [state, setState] = useState<PromptOptions | null>(null);
  const resolveRef = useRef<((value: string | null) => void) | null>(null);

  const prompt = useCallback((opts: PromptOptions) => {
    setState(opts);
    return new Promise<string | null>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const settle = useCallback((value: string | null) => {
    setState(null);
    resolveRef.current?.(value);
    resolveRef.current = null;
  }, []);

  const dialog = (
    <PromptDialog
      open={state !== null}
      title={state?.title ?? ''}
      description={state?.description}
      label={state?.label}
      placeholder={state?.placeholder}
      initialValue={state?.initialValue}
      hint={state?.hint}
      confirmLabel={state?.confirmLabel}
      cancelLabel={state?.cancelLabel}
      minLength={state?.minLength}
      maxLength={state?.maxLength}
      multiline={state?.multiline}
      mono={state?.mono}
      validate={state?.validate}
      onSubmit={(v) => settle(v)}
      onCancel={() => settle(null)}
    />
  );

  return [prompt, dialog, state !== null];
}
